"use client";

import { useEffect, useRef, useCallback } from "react";
import { useAuthStore } from "@/stores/auth-store";
import { useWhatsAppStore } from "@/stores/whatsapp-store";
import { useInboxStore } from "@/stores/inbox-store";
import {
  getSocket,
  resetSocket,
  connectSocket,
  disconnectSocket,
} from "@/lib/socket";
import type { WhatsAppAccountStatus, WhatsAppMessage } from "@/types";
import type { MessageStatus } from "@/lib/whatsapp-api";

interface SocketProviderProps {
  children: React.ReactNode;
}

export function SocketProvider({ children }: SocketProviderProps) {
  const isAuthenticated = useAuthStore((state) => state.isAuthenticated);
  const token = useAuthStore((state) => state.token);
  const whatsappEnabled = useAuthStore((state) => state.user?.feature_flags?.whatsapp_enabled ?? false);
  const setConnected = useWhatsAppStore((state) => state.setConnected);
  const fetchStatus = useWhatsAppStore((state) => state.fetchStatus);
  const onMessageReceived = useWhatsAppStore((state) => state.onMessageReceived);
  const onAccountStatusChanged = useWhatsAppStore((state) => state.onAccountStatusChanged);
  const inboxOnMessageReceived = useInboxStore((state) => state.onMessageReceived);
  const inboxOnMessageStatusUpdate = useInboxStore((state) => state.onMessageStatusUpdate);
  const fetchAccountCounts = useInboxStore((state) => state.fetchAccountCounts);

  // Use refs to avoid stale closures
  const setConnectedRef = useRef(setConnected);
  const fetchStatusRef = useRef(fetchStatus);
  const onMessageReceivedRef = useRef(onMessageReceived);
  const onAccountStatusChangedRef = useRef(onAccountStatusChanged);
  const inboxOnMessageReceivedRef = useRef(inboxOnMessageReceived);
  const inboxOnMessageStatusRef = useRef(inboxOnMessageStatusUpdate);
  const fetchAccountCountsRef = useRef(fetchAccountCounts);

  // Update refs when functions change
  useEffect(() => {
    setConnectedRef.current = setConnected;
    fetchStatusRef.current = fetchStatus;
    onMessageReceivedRef.current = onMessageReceived;
    onAccountStatusChangedRef.current = onAccountStatusChanged;
    inboxOnMessageReceivedRef.current = inboxOnMessageReceived;
    inboxOnMessageStatusRef.current = inboxOnMessageStatusUpdate;
    fetchAccountCountsRef.current = fetchAccountCounts;
  }, [setConnected, fetchStatus, onMessageReceived, onAccountStatusChanged, inboxOnMessageReceived, inboxOnMessageStatusUpdate, fetchAccountCounts]);

  // Event handlers using refs -- no room joining needed, server handles it
  const handleConnect = useCallback(() => {
    console.log("[Socket] Connected");
    setConnectedRef.current(true);
    // Fetch initial status -- server auto-joins rooms based on user's account assignments
    fetchStatusRef.current();
    // Load per-account counts for inbox tab badges
    fetchAccountCountsRef.current();
  }, []);

  const handleDisconnect = useCallback((reason: string) => {
    console.log("[Socket] Disconnected:", reason);
    setConnectedRef.current(false);
  }, []);

  const handleAccountStatus = useCallback(
    (data: { accountId: string; status: WhatsAppAccountStatus; phoneNumber?: string }) => {
      console.log("[Socket] Account status:", data);
      onAccountStatusChangedRef.current(data.accountId, data.status, data.phoneNumber);
    },
    []
  );

  const handleMessageReceived = useCallback(
    (data: { accountId: string; conversationId: string; message: WhatsAppMessage }) => {
      console.log("[Socket] Message received:", data.accountId, data.conversationId);
      // Update global whatsapp store (existing)
      onMessageReceivedRef.current(data.conversationId, data.message);
      // Update per-account inbox store (new)
      inboxOnMessageReceivedRef.current(data.accountId, data.conversationId, data.message);
    },
    []
  );

  const handleMessageStatus = useCallback(
    (data: { accountId: string; messageId: string; status: MessageStatus }) => {
      inboxOnMessageStatusRef.current(data.accountId, data.messageId, data.status);
    },
    []
  );

  useEffect(() => {
    if (!isAuthenticated || !whatsappEnabled || !token) {
      // Disconnect and reset when not authenticated, WhatsApp is disabled, or no token
      disconnectSocket();
      resetSocket();
      setConnectedRef.current(false);
      return;
    }

    // Reset existing socket (ensures new token is used on reconnect)
    resetSocket();

    // Create new socket with the user's Sanctum token
    const socket = getSocket(token);

    // Register event handlers
    socket.on("connect", handleConnect);
    socket.on("disconnect", handleDisconnect);
    socket.on("account:status", handleAccountStatus);
    socket.on("message:received", handleMessageReceived);
    socket.on("message:status", handleMessageStatus);
    socket.on("connect_error", (error) => {
      console.error("[Socket] Connection error:", error.message);
    });

    // Connect
    console.log("[Socket] Connecting with user token...");
    connectSocket();

    // Cleanup: remove listeners and disconnect
    return () => {
      socket.off("connect", handleConnect);
      socket.off("disconnect", handleDisconnect);
      socket.off("account:status", handleAccountStatus);
      socket.off("message:received", handleMessageReceived);
      socket.off("message:status", handleMessageStatus);
      socket.off("connect_error");
    };
  }, [isAuthenticated, whatsappEnabled, token, handleConnect, handleDisconnect, handleAccountStatus, handleMessageReceived, handleMessageStatus]);

  return <>{children}</>;
}
